import {
	Button,
	CircularProgress,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Profile } from "../../models/Profile";
import { upsertProfile } from "../../repositories/ProfilesRepository";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const CreateProfile = ({ show, onHide, suffixTitle }: Props) => {
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [message, setMessage] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Profile>({ defaultValues: INITIAL_VALUES });

	const createProfile = useMutation({
		mutationFn: upsertProfile,
		onSuccess: () => {
			setMessage("The profile was created successfully");
			setOpenSnackbar(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<Profile> = async (data) => {
		setIsSubmitLoading(true);
		await createProfile.mutate({
			ProfileId: data.ProfileId!,
			ProfileName: data.ProfileName,
		});
	};

	const handleClose = (
		event: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`New Profile${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Grid2 container>
				<Grid2>
					<MesfModal
						title="NEW PROFILE"
						open={show}
						handleClose={() => onHide(false)}
						id="create-profile-modal"
						maxWidth="md"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px" }}>
								<Grid2 container spacing={1}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<TextField
											{...register("ProfileName", {
												required: true,
												validate: {
													notWhitespace: (value) =>
														value.trim() !== "" ||
														"Profile name cannot be only whitespace",
												},
											})}
											label="Profile name"
											variant="outlined"
											error={!!errors.ProfileName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>{" "}
										{errors.ProfileName?.type === "required" && (
											<span style={{ fontSize: 12, color: "#F44336" }}>
												Profile name is required
											</span>
										)}
										{errors.ProfileName?.type === "notWhitespace" && (
											<span style={{ fontSize: 12, color: "#F44336" }}>
												{errors.ProfileName.message}
											</span>
										)}
									</Grid2>
								</Grid2>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid2 container spacing={2} justifyContent="flex-end">
									<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid2>
								</Grid2>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid2>
			</Grid2>
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					{message}
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Profile"
			/>
		</>
	);
};
